import React, { useEffect, useState } from "react";
import api from "@/api/client";

/**
 * Compact inline SVG sparkline for a single article's 30-day health trend.
 * Lazy loads on mount; renders "—" if no snapshots exist yet.
 */
export default function Sparkline({ contentId, width = 100, height = 28, days = 30 }) {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/trends/article/${contentId}/sparkline?days=${days}`);
        if (!cancelled) setPoints(data.points || []);
      } catch {
        if (!cancelled) setPoints([]);
      }
    })();
    return () => { cancelled = true; };
  }, [contentId, days]);

  if (points === null) {
    return <div style={{ width, height }} className="animate-pulse bg-slate-800/60 rounded" />;
  }
  if (points.length === 0) {
    return <span className="text-[10px] font-mono text-slate-600" title="No snapshots yet">— no trend</span>;
  }
  if (points.length === 1) {
    return (
      <div className="flex items-center gap-1" data-testid={`sparkline-${contentId}`}>
        <svg width={width} height={height} className="overflow-visible">
          <circle cx={width / 2} cy={height / 2} r="2.5" fill="#e6b800" />
        </svg>
        <span className="text-[10px] font-mono text-sky-300">{points[0].health}</span>
      </div>
    );
  }

  const minH = Math.min(...points.map((p) => p.health));
  const maxH = Math.max(...points.map((p) => p.health));
  const range = Math.max(1, maxH - minH);
  const stepX = width / (points.length - 1);
  const pad = 3;
  const usableH = height - pad * 2;
  const coords = points.map((p, i) => {
    const x = i * stepX;
    const y = pad + usableH - ((p.health - minH) / range) * usableH;
    return [x, y];
  });
  const path = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const last = points[points.length - 1];
  const first = points[0];
  const delta = last.health - first.health;
  const trending = delta >= 0 ? "#4ade80" : "#f87171";

  return (
    <div className="flex items-center gap-1.5" data-testid={`sparkline-${contentId}`} title={`${points.length} snapshots · ${first.health} → ${last.health}`}>
      <svg width={width} height={height} className="overflow-visible">
        <path d={path} fill="none" stroke={trending} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="2" fill={trending} />
      </svg>
      <span className="text-[10px] font-mono" style={{ color: trending }}>
        {delta >= 0 ? "+" : ""}{delta}
      </span>
    </div>
  );
}
